'use client';

import type {
  AppShellSidebarNavGroup,
  AppShellSidebarNavItem,
} from '@/components/layout/dashboard/app-shell-sidebar-nav.types';
import {
  ADMIN_PANEL_SIDEBAR_GROUPS,
  type AdminPanelSection,
} from '@/domains/admin-panel/admin-panel-sidebar.config';
import { useAuthContext } from '@/hooks/use-auth';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { useMemo } from 'react';

function isAdminPanelSectionVisible(
  section: AdminPanelSection,
  {
    isNebulyAdmin,
    enableAdminExportLogs,
  }: {
    isNebulyAdmin: boolean;
    enableAdminExportLogs: boolean;
  },
): boolean {
  switch (section) {
    case 'caching':
    case 'ingestion':
    case 'cleanup':
    case 'analysed-data':
    case 'feature-flags':
      return isNebulyAdmin;
    case 'export-logs':
      return enableAdminExportLogs;
    default:
      return false;
  }
}

export function useAdminPanelSidebarItems(): AppShellSidebarNavItem<AdminPanelSection>[] {
  return useAdminPanelSidebarGroups().flatMap((group) => group.items);
}

export function useAdminPanelSidebarGroups(): AppShellSidebarNavGroup<AdminPanelSection>[] {
  const { isNebulyAdmin } = useAuthContext();
  const { enableAdminExportLogs } = useEnvironmentContext();

  return useMemo(
    () =>
      ADMIN_PANEL_SIDEBAR_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          isAdminPanelSectionVisible(item.id, { isNebulyAdmin, enableAdminExportLogs }),
        ),
      })).filter((group) => group.items.length > 0),
    [enableAdminExportLogs, isNebulyAdmin],
  );
}

export function useDefaultAdminPanelSection(): AdminPanelSection {
  const items = useAdminPanelSidebarItems();
  return items[0]?.id ?? 'caching';
}

export function useCanAccessAdminPanel(): boolean {
  const { isCompanyAdmin } = useAuthContext();
  const items = useAdminPanelSidebarItems();
  return isCompanyAdmin && items.length > 0;
}
